import { max } from "date-fns";
import {
  Absence,
  AbsenceType,
  ApprovalStatus,
  CreateAbsenceInput,
} from "../../../API";
import { User } from "../../../hooks";
import { toAWSDate } from "../../../lib";
import { validTimeOff } from "../utils";

type DescriptionProps = {
  amountOfDaysWithinPeriod: number;
  description: string;
};

export const getDescription = ({
  amountOfDaysWithinPeriod,
  description,
}: DescriptionProps) => {
  let updatedDescription = description ?? "";
  if (amountOfDaysWithinPeriod > 0) {
    const holidayExclusionNote = `\n(${amountOfDaysWithinPeriod} Holiday day${
      amountOfDaysWithinPeriod > 1 ? "s" : ""
    } ha${
      amountOfDaysWithinPeriod == 1 ? "s" : "ve"
    } been excluded from the request.)`;

    if (
      // if we haven't written the note, write it
      !description.match(
        /\r?\n\(\d+ Holiday days? ha(?:s|ve) been excluded from the request\.\)/g
      )?.length
    ) {
      updatedDescription = description + holidayExclusionNote;
    } // if it does match, it is possible there are now MORE holiday days to exclude so we must replace the message
    else
      updatedDescription = description.replace(
        /\r?\n\(\d+ Holiday days? ha(?:s|ve) been excluded from the request\.\)/g,
        holidayExclusionNote
      );
  }

  if (amountOfDaysWithinPeriod == 0) {
    // going from 1 holiday to exclude, to 0
    updatedDescription = description.replace(
      /\r?\n\(\d+ Holiday days? ha(?:s|ve) been excluded from the request\.\)/g,
      ""
    );
  }

  return updatedDescription;
};

type ExistingAbsenceProps = {
  absences: Absence[];
  currentAbsenceId?: string | null;
  formStartDate: string;
  formEndDate: string;
};

export const checkExistingAbsences = ({
  absences,
  currentAbsenceId,
  formStartDate,
  formEndDate,
}: ExistingAbsenceProps) => {
  if (!absences || absences.length === 0) return false;

  return !!absences?.find((a) => {
    // If Updating current Absence ignore
    if (a.id === currentAbsenceId) return false;

    const startDate = a.startDate;
    const endDate = a.endDate;

    const newStartDate = toAWSDate(new Date(formStartDate)) as string;
    const newEndDate = toAWSDate(new Date(formEndDate)) as string;
    const isValidRequest = validTimeOff.includes(a?.approvalStatus ?? "");

    return (
      // check if starts before and end is within the existing range
      ((newStartDate <= startDate && newEndDate >= startDate) ||
        // check if end is after and start is within the existing range
        (newEndDate >= endDate && newStartDate <= endDate) ||
        // check if start and end is within the existing range
        (newStartDate >= startDate && newEndDate < endDate) ||
        // check if start is before and end is after the range - existing range is within the new on
        (newStartDate < startDate && newEndDate >= endDate)) &&
      isValidRequest
    );
  });
};

const DEFAULT_AMOUNT = 1;
export type FormFields = CreateAbsenceInput;

export const mapDefaultValues = (
  value?: FormFields,
  user?: User,
  employeeID?: string,
  minDate = new Date()
): FormFields => ({
  id: value?.id,
  startDate: value?.startDate ?? max([minDate, new Date()]).toISOString(),
  endDate: value?.endDate ?? max([minDate, new Date()]).toISOString(),
  amount: value?.amount ?? DEFAULT_AMOUNT,
  description: value?.description ?? "",
  hoursTaken: value?.hoursTaken ?? 0,
  type: value?.type ?? AbsenceType.holiday,
  employeeID: value?.employeeID ?? employeeID ?? user?.id ?? "",
  approvalStatus: value?.approvalStatus ?? ApprovalStatus.pending,
  approvedByClient: value?.approvedByClient ?? false,
});
